import { useEffect, useMemo, useRef } from 'react';
import type { Object3D } from 'three';
import { createViewport, type Viewport } from '@/render/scene';
import { buildHouse, type BuiltHouse } from '@/render/builders/buildHouse';
import { buildDebugOverlays } from '@/debug/overlays';
import { useConfigStore } from '@/state/configStore';
import { useConstraintsStore } from '@/state/constraintsStore';
import { useUiStore } from '@/state/uiStore';
import { useHingesStore } from '@/state/hingesStore';
import { encodeToHash, tryDecodeFromHash } from '@/model/serialization';
import { generateHouse } from '@/generator/generateHouse';
import { ConstraintsPanel } from '@/ui/panels/ConstraintsPanel';
import { HingesPanel } from '@/ui/panels/HingesPanel';
import { Topbar } from '@/ui/overlays/Topbar';
import { ViewModes } from '@/ui/overlays/ViewModes';
import { StoryPeel } from '@/ui/overlays/StoryPeel';
import { SunSlider } from '@/ui/overlays/SunSlider';
import { ActionBar } from '@/ui/overlays/ActionBar';
import { Hud } from '@/ui/overlays/Hud';
import { Toasts } from '@/ui/overlays/Toasts';
import { EgoOverlay } from '@/ui/overlays/EgoOverlay';
import '@/styles/global.css';
import '@/styles/app.css';

export function App() {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const viewportRef = useRef<Viewport | null>(null);
  const builtRef = useRef<BuiltHouse | null>(null);
  const debugRef = useRef<Object3D | null>(null);

  // Bootstrap on mount: decode URL hash if present, else seed-and-generate.
  useEffect(() => {
    const hash = window.location.hash;
    if (hash && hash.length > 1) {
      const decoded = tryDecodeFromHash(hash);
      if (decoded.ok) {
        useConstraintsStore.getState().setSeed(decoded.value.seed);
        const input = useConstraintsStore.getState().toGenerationInput();
        useConfigStore.getState().setResult(
          {
            config: decoded.value,
            score: { total: decoded.value.score, dimensions: [] },
            rejections: [],
          },
          JSON.stringify(input),
        );
        return;
      }
      console.warn('Failed to decode share link:', decoded.error);
    }
    const input = useConstraintsStore.getState().toGenerationInput();
    const result = generateHouse(input);
    useConfigStore.getState().setResult(result, JSON.stringify(input));
  }, []);

  // Viewport lifecycle.
  useEffect(() => {
    if (!containerRef.current) return;
    const theme = useUiStore.getState().theme;
    const viewport = createViewport({
      container: containerRef.current,
      theme,
      idleDelayMs: 3500,
      // Browser released the pointer lock (ESC) → mirror it into the
      // React store so overlays / buttons update and subsequent
      // enters/exits stay consistent.
      onEgoExit: () => {
        useUiStore.getState().setEgoMode(false);
      },
    });
    viewportRef.current = viewport;
    viewport.setAutoRotate(useUiStore.getState().autoRotate);
    return () => {
      viewport.dispose();
      viewportRef.current = null;
    };
  }, []);

  // Theme changes.
  const theme = useUiStore((s) => s.theme);
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    viewportRef.current?.setTheme(theme);
  }, [theme]);

  // Auto-rotate.
  const autoRotate = useUiStore((s) => s.autoRotate);
  useEffect(() => {
    viewportRef.current?.setAutoRotate(autoRotate);
  }, [autoRotate]);

  // Sun position (time of day) → directional light arc + tint.
  const sun = useUiStore((s) => s.sun);
  useEffect(() => {
    viewportRef.current?.setSun(sun);
  }, [sun]);

  // Show/hide site context (lot plate, fence, mailbox).
  const showContext = useUiStore((s) => s.showContext);
  useEffect(() => {
    viewportRef.current?.setShowContext(showContext);
  }, [showContext]);

  // View mode — map to camera + peel. Peel is always live so the slider
  // works in exterior view too (top-down ignores peel — it's always a plan
  // view of story 0).
  const viewMode = useUiStore((s) => s.viewMode);
  const storyPeel = useUiStore((s) => s.storyPeel);
  useEffect(() => {
    const v = viewportRef.current;
    if (!v) return;
    v.setCameraMode(viewMode === 'topDown' ? 'topDown' : 'perspective');
    // Peel is live in every view, including top-down where peel=1 reveals
    // the top-story plan from above.
    v.setStoryPeel(storyPeel);
    v.debugRoot.visible = viewMode === 'wireframeDebug' || useUiStore.getState().debug;
  }, [viewMode, storyPeel]);

  // Rebuild house subtree on config generation.
  const generation = useConfigStore((s) => s.generation);
  const config = useConfigStore((s) => s.config);
  const debugOn = useUiStore((s) => s.debug);

  useEffect(() => {
    const v = viewportRef.current;
    if (!v || !config) return;

    // Dispose old build.
    if (builtRef.current) {
      v.houseRoot.remove(builtRef.current.group);
      builtRef.current.dispose();
      builtRef.current = null;
    }
    if (debugRef.current) {
      v.debugRoot.remove(debugRef.current);
      debugRef.current = null;
    }

    const built = buildHouse(config);
    builtRef.current = built;
    v.houseRoot.add(built.group);

    const debugOverlays = buildDebugOverlays(config);
    debugRef.current = debugOverlays;
    v.debugRoot.add(debugOverlays);
    v.debugRoot.visible = debugOn || useUiStore.getState().viewMode === 'wireframeDebug';

    // Push the freshly collected hinges into the global registry so the
    // right-side panel can render sliders for them. We call this BEFORE
    // applying initial values so the apply pass sees the new ids.
    useHingesStore.getState().setDefs(built.hinges);
    // The viewport's per-hinge animation state is scoped to the live
    // scene graph, so wipe it whenever the graph itself is replaced —
    // otherwise stale ids keep spinning against nodes that no longer
    // exist (no visible effect, but a slow leak of Map entries).
    v.resetHingeAnimations();
    // Reapply any preserved hinge values + inversion flags to the new
    // scene graph. The first call per id snaps the visual to match
    // the stored value (see scene.ts setHingeAngle) so nothing tweens
    // in from zero on a rebuild.
    const { values: hingeValues, inverted: hingeInverted } = useHingesStore.getState();
    for (const def of built.hinges) {
      v.setHingeAngle(def.id, hingeValues[def.id] ?? 0, hingeInverted[def.id] ?? false);
    }

    v.setStoryPeel(useUiStore.getState().storyPeel);
    v.setShowContext(useUiStore.getState().showContext);

    // If the user is currently in ego mode, rebuild the collision
    // octree against the fresh house so the capsule-vs-world solver
    // doesn't reference stale triangles from the previous build. The
    // octree deliberately excludes hinge leaves, so we don't need to
    // pass — or later update with — any hinge values.
    if (useUiStore.getState().egoMode) {
      v.rebuildCollision();
    }

    const payload = encodeToHash(config);
    history.replaceState(null, '', `#${payload}`);
  }, [generation, config, debugOn]);

  // Live hinge value updates — push new targets into the viewport
  // whenever a slider moves OR the user toggles the "invert direction"
  // switch on a hinge. The viewport interpolates between target values
  // in its render tick, so this subscriber just forwards target-state;
  // it no longer needs to rebuild the collision octree on threshold
  // crossings (hinge leaves are permanently excluded from collision).
  useEffect(() => {
    return useHingesStore.subscribe((state, prev) => {
      const v = viewportRef.current;
      if (!v) return;
      const allIds = new Set<string>([
        ...Object.keys(state.values),
        ...Object.keys(state.inverted),
      ]);
      for (const id of allIds) {
        const valueChanged = state.values[id] !== prev?.values[id];
        const invertedChanged = (state.inverted[id] ?? false) !== (prev?.inverted[id] ?? false);
        if (valueChanged || invertedChanged) {
          v.setHingeAngle(id, state.values[id] ?? 0, state.inverted[id] ?? false);
        }
      }
    });
  }, []);

  // Ego mode toggle → tell the viewport to enter or exit. The
  // viewport in turn requests pointer lock (must run inside the
  // user-gesture click handler, which `setEgoMode(true)` is called
  // from in ViewModes) and swaps the camera.
  const egoMode = useUiStore((s) => s.egoMode);
  useEffect(() => {
    const v = viewportRef.current;
    if (!v) return;
    if (egoMode) {
      // Rebuild the collision octree once at entry so the capsule
      // starts against fresh triangles (a previous ego session may
      // have been against a house that has since been regenerated).
      v.rebuildCollision();
      v.enterEgoMode();
    } else {
      v.exitEgoMode();
    }
  }, [egoMode]);

  const getHouseRoot = useMemo(
    () => () => viewportRef.current?.houseRoot ?? null,
    [],
  );

  return (
    <div className="app" data-theme={theme} data-ego={egoMode ? 'on' : 'off'}>
      <div className="viewport" ref={containerRef} aria-label="3D viewport" role="img" />
      <Topbar />
      <ConstraintsPanel />
      <HingesPanel />
      <ViewModes />
      <StoryPeel />
      <SunSlider />
      <ActionBar getHouseRoot={getHouseRoot} />
      <Hud />
      <Toasts />
      <EgoOverlay />
    </div>
  );
}
